import { useState } from "react";
import DeleteModal from "@via/components/delete-modal/delete-modal";
import {
  useGetCancelAccountPreview,
  useCancelAccount,
} from "@via/pages/organization/organization.api.tsx";
import { useDeactivateUser } from "@via/pages/account/profile/user-profile/user-profile.api.tsx";

const DeactivateAccount = () => {
  // The server resolves which organization cancel-account acts on (#1136). Reading
  // userOrganizations[0] here picked an arbitrary membership for a user of several.
  const { data: preview, isLoading: isPreviewLoading } =
    useGetCancelAccountPreview();
  const resolved = preview?.status === "resolved" ? preview : null;
  const isOrgAdmin = Boolean(resolved?.isOrgAdmin);
  const isLastOrgOwner = Boolean(resolved?.isLastOrgAdmin);
  const organizationName = resolved?.organization.name;
  const otherOrganizationCount = resolved?.otherOrganizationCount ?? 0;
  const [isAccountToBeDeleted, setAccountToBeDeleted] =
    useState<boolean>(false);
  const [confirmOrgDeactivation, setConfirmOrgDeactivation] = useState(false);

  const {
    mutateAsync: deactivateAccount,
    isPending: isdeactivateAccountLoading,
  } = useCancelAccount();

  const { mutateAsync: deactivateUser, isPending: isDeactivateUserLoading } =
    useDeactivateUser();

  const handleDeactivation = async () => {
    if (isOrgAdmin) {
      await deactivateAccount({
        deactivateOrganization: isLastOrgOwner || confirmOrgDeactivation,
      });
    } else {
      await deactivateUser(undefined);
    }
  };

  return (
    <>
      <div className="flex flex-row gap-10 flex-1 pb-24 overflow-y-auto">
        <div className="flex-1">
          <h2 className="via-h2">Deactivate Account</h2>
          <p>This action is irreversible. Please proceed with caution.</p>
        </div>
      </div>
      <>
        <div className="fixed bottom-0 left-0 right-0 z-10 bg-white border-t border-gray-200 px-6 py-4 flex justify-end">
          <button
            disabled={isPreviewLoading || !preview}
            onClick={() => setAccountToBeDeleted(true)}
            className="inline-flex justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600">
            Deactivate Account
          </button>
        </div>
        {isAccountToBeDeleted && (
          <DeleteModal
            item={isAccountToBeDeleted}
            setItem={setAccountToBeDeleted}
            deleteFunction={handleDeactivation}
            isLoading={isdeactivateAccountLoading || isDeactivateUserLoading}
            title="Deactivate Account"
            confirmText="Deactivate"
            message={() => (
              <div className="space-y-4 text-sm text-gray-700">
                <p>Deactivating your account will:</p>
                <ul className="list-disc list-inside pl-4 space-y-1">
                  <li>
                    <strong>Revoke access</strong> to your personal and
                    organization-related data.
                  </li>
                  <li>
                    Retain your data for <strong>90 days</strong> before
                    permanent deletion, as per our retention policy.
                  </li>
                  {isOrgAdmin && isLastOrgOwner ? (
                    <>
                      <li>
                        <strong>
                          Deactivate the subscription of {organizationName}
                        </strong>
                        .
                      </li>
                      <li>
                        Since you are the{" "}
                        <strong>last remaining Org Owner</strong> of{" "}
                        {organizationName}, it will be
                        <strong className="text-red-600">
                          {" "}
                          permanently deactivated
                        </strong>
                        .
                      </li>
                      <p className="pt-4 text-red-600 font-medium">
                        Warning: Deactivating your account will deactivate the
                        entire organization.
                      </p>
                    </>
                  ) : isOrgAdmin ? (
                    <>
                      <li>
                        <strong>{organizationName} will remain active</strong>,
                        and other Org Owners will still retain access.
                      </li>
                      <p className="pt-4 text-yellow-600 font-medium">
                        Note: If you want to deactivate the entire organization,
                        confirm below.
                      </p>
                      <label className="flex items-center gap-2 pt-2">
                        <input
                          type="checkbox"
                          checked={confirmOrgDeactivation}
                          onChange={e =>
                            setConfirmOrgDeactivation(e.target.checked)
                          }
                          className="block h-6 w-6 rounded-md border-0 text-indigo-600 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        />
                        <span className="text-sm text-red-600 font-medium">
                          I want to deactivate {organizationName} and cancel its
                          subscription.
                        </span>
                      </label>
                    </>
                  ) : preview?.status === "ambiguous" ? (
                    <p className="pt-4 text-yellow-600 font-medium">
                      You belong to several organizations. Your account will be
                      deactivated and every organization stays active. Contact
                      support to close an organization.
                    </p>
                  ) : null}
                  {isOrgAdmin && otherOrganizationCount > 0 ? (
                    <li>
                      Remove you from your{" "}
                      {otherOrganizationCount === 1
                        ? "other organization, which stays"
                        : `${otherOrganizationCount} other organizations, which stay`}{" "}
                      active.
                    </li>
                  ) : null}
                </ul>
              </div>
            )}
          />
        )}
      </>
    </>
  );
};

export default DeactivateAccount;
